import type { TFunction } from "i18next";
import { type ReactNode, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { localStorageService, teamTableSortingKey } from "../../services/LocalStorageService";
import { getPrimaryJobLabel } from "../../utils/jobs";
import { EffectiveEmployeeStatus, type TeamNurse } from "../../utils/models";
import ColorDot from "../UIToolkit/molecules/ColorDot";
import type { SelectionAction } from "../UIToolkit/molecules/SelectionActionBar";
import type { HolidayGroup, HolidayGroupChangePerson } from "./Holidays/holidayApi";
import { holidayGroupColor, holidayGroupLabel } from "./Holidays/holidayFormat";
import { TEAM_HOLIDAY_DAY_PART_FIELD, TEAM_HOLIDAY_GROUP_FIELD } from "./teamColumnVisibility";
import { clearTeamUrlState, parseTeamUrlState, writeTeamUrlState } from "./teamUrlState";

// --- Opening the roster from the Holidays tab ---

/**
 * Who to show on the roster: one group (or HOLIDAY_NONE for Not assigned), a day part (a day part, HOLIDAY_NONE
 * for No day part, or null for every day part) and a job (null for every job).
 */
export type HolidayStaffFilter = { groupId: string; dayPartKey: string | null; jobName: string | null };

/**
 * Everyone the Group balance counts as "active": the server's active people include staff on leave or
 * suspended, so the roster must show those statuses too for the list to match the number clicked.
 */
const ACTIVE_STATUSES = [EffectiveEmployeeStatus.Active, EffectiveEmployeeStatus.OnLeave, EffectiveEmployeeStatus.Suspended];

/** The Team roster's filters for these people, with every other filter cleared (agency staff included). */
export const holidayStaffSearchParams = (current: URLSearchParams, { groupId, dayPartKey, jobName }: HolidayStaffFilter): URLSearchParams => {
	const cleared = clearTeamUrlState(current);
	return writeTeamUrlState(cleared, {
		...parseTeamUrlState(cleared),
		selectedStatuses: new Set(ACTIVE_STATUSES),
		selectedAgencies: new Set(),
		selectedJobNames: jobName === null ? [] : [jobName],
		selectedHolidayGroups: new Set([groupId]),
		selectedHolidayDayParts: dayPartKey === null ? new Set() : new Set([dayPartKey]),
	});
};

/**
 * Opens the Team roster on some of the Holidays tab's people, with the holiday columns turned on.
 *
 * The columns are turned on through the grid's saved column layout: the roster grid isn't mounted while the
 * Holidays tab is showing, and reads that layout when it mounts.
 */
export const useShowHolidayStaffOnTeam = (showTeamTab: () => void) => {
	const [, setSearchParams] = useSearchParams();
	const { userInfo } = useAuth();
	const userId = userInfo?.id;
	return useCallback(
		(filter: HolidayStaffFilter) => {
			if (userId) {
				const stored = localStorageService.getTableColumnVisibility(teamTableSortingKey, userId);
				localStorageService.saveTableColumnVisibility(
					teamTableSortingKey,
					{ ...stored, [TEAM_HOLIDAY_GROUP_FIELD]: true, [TEAM_HOLIDAY_DAY_PART_FIELD]: true },
					userId,
				);
			}
			setSearchParams((current) => holidayStaffSearchParams(current, filter));
			showTeamTab();
		},
		[userId, setSearchParams, showTeamTab],
	);
};

// --- Roster row to group change popup person ---

/** A roster row as the group change popup shows it: the department-ordered name and their primary job. */
export const toHolidayChangePerson = (nurse: TeamNurse, formatPersonName: (person: TeamNurse) => string): HolidayGroupChangePerson => ({
	nurseId: nurse.id,
	name: formatPersonName(nurse),
	// No job assigned shows no job line at all.
	jobLabel: getPrimaryJobLabel(nurse) ?? null,
});

// --- Bulk action ---

/** The selection bar's "Set holiday group" menu: each group, then Not assigned. Picking one opens the popup. */
export const buildHolidayGroupBulkAction = ({
	t,
	groups,
	onPick,
	leadingIcon,
}: {
	t: TFunction;
	groups: readonly HolidayGroup[];
	/** A group id, or null for Not assigned. */
	onPick: (groupId: string | null) => void;
	leadingIcon: ReactNode;
}): SelectionAction => ({
	id: "holiday-group",
	label: t("holidays.bulk.setGroup"),
	leadingIcon,
	menuItems: [
		...groups.map((group) => ({
			id: group.id,
			label: holidayGroupLabel(t, group),
			leading: <ColorDot color={holidayGroupColor(groups, group.id).dot} size="sm" />,
			onSelect: () => onPick(group.id),
		})),
		{ id: "none", label: t("holidays.group.notAssigned"), groupStart: true, onSelect: () => onPick(null) },
	],
});
